import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, HttpError } from "../api";
import { useAuth } from "../auth";
import type { OzonCabinet, OzonProduct, OzonRefreshResponse } from "../types";
import { formatMSK } from "../utils/dates";

export default function OzonProducts() {
  const { user } = useAuth();
  const canUpload = user?.role === "admin" || user?.role === "marketplace_manager";

  const [products, setProducts] = useState<OzonProduct[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastOk, setLastOk] = useState<string | null>(null);
  const [apiBusy, setApiBusy] = useState(false);
  const [apiResult, setApiResult] = useState<OzonRefreshResponse | null>(null);
  // Кабинеты Ozon (юрлица): для фильтра справочника (табы) и выбора кабинета
  // при ручной заливке CSV.
  const [cabinets, setCabinets] = useState<OzonCabinet[]>([]);
  const [filterClientId, setFilterClientId] = useState<number | null>(null);
  const [uploadClientId, setUploadClientId] = useState<string>("");

  const cabinetName = (cid: number): string =>
    cabinets.find((c) => c.ozon_client_id === cid)?.name ?? `#${cid}`;

  const refresh = useCallback(async () => {
    try {
      const list = await api.listOzonProducts(500, filterClientId);
      setProducts(list);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : "сбой");
    }
  }, [filterClientId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    api
      .listCabinets()
      .then((list) => {
        setCabinets(list);
        if (list.length === 1) setUploadClientId(String(list[0].ozon_client_id));
      })
      .catch(() => setCabinets([]));
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file || !uploadClientId) return;
    setBusy(true);
    setErr(null);
    setLastOk(null);
    try {
      const r = await api.uploadOzonProducts(file, Number(uploadClientId));
      setLastOk(`Загружено: новых ${r.inserted}, обновлено ${r.updated}`);
      setFile(null);
      (document.getElementById("ozon-upload-input") as HTMLInputElement | null)?.value &&
        ((document.getElementById("ozon-upload-input") as HTMLInputElement).value = "");
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
    } finally {
      setBusy(false);
    }
  }

  async function onRefreshFromApi() {
    setApiBusy(true);
    setErr(null);
    setApiResult(null);
    setLastOk(null);
    try {
      const r = await api.refreshOzonProducts();
      setApiResult(r);
      await refresh();
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.status}: ${e.message}` : "сбой");
    } finally {
      setApiBusy(false);
    }
  }

  return (
    <div className="container">
      <h1>Справочник Ozon</h1>
      <p className="muted">
        Загрузите выгрузку товаров из кабинета Ozon (файл{" "}
        <span className="mono">ozn_tov.csv</span>). Система сохранит артикулы и
        штрихкоды; старые товары не удаляются, новые — добавляются, совпадающие — обновляются.
      </p>

      {canUpload && (
        <div className="card">
          <h2>Обновить справочник</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Можно нажать <b>«Загрузить из Ozon»</b> — система сама закажет отчёт «Товары»
            через Ozon Seller API по всем настроенным кабинетам, дождётся его и прогонит через
            тот же импорт. Либо, по старинке, загрузить файл выгрузки <span className="mono">ozn_tov.csv</span>.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button
              className="primary"
              type="button"
              onClick={onRefreshFromApi}
              disabled={apiBusy || busy}
              title="Запрос к Ozon Seller API — занимает до нескольких минут"
            >
              {apiBusy ? "Готовим отчёт из Ozon…" : "Загрузить из Ozon"}
            </button>
            <span className="muted">или</span>
            <form onSubmit={onSubmit} style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              <select
                value={uploadClientId}
                onChange={(e) => setUploadClientId(e.target.value)}
                disabled={busy || apiBusy}
                title="кабинет Ozon (юрлицо), в справочник которого грузим файл"
              >
                <option value="" disabled>
                  юрлицо *
                </option>
                {cabinets.map((c) => (
                  <option key={c.ozon_client_id} value={c.ozon_client_id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <input
                id="ozon-upload-input"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={busy || apiBusy}
              />
              <button className="primary" disabled={busy || apiBusy || !file || !uploadClientId}>
                Загрузить файл
              </button>
            </form>
          </div>
          {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
          {lastOk && <div className="ok" style={{ marginTop: 12 }}>{lastOk}</div>}
          {apiResult && (
            <div className="ok" style={{ marginTop: 12 }}>
              <div>
                Синхронизация по API завершена: <b>{apiResult.total_inserted}</b> новых,{" "}
                <b>{apiResult.total_updated}</b> обновлено.
              </div>
              <ul style={{ margin: "8px 0 0 18px" }}>
                {apiResult.per_account.map((a) => (
                  <li key={a.client_id}>
                    Кабинет <span className="mono">{a.client_id}</span>:{" "}
                    {a.error ? (
                      <span className="error">ошибка — {a.error}</span>
                    ) : (
                      <>
                        новых {a.inserted}, обновлено {a.updated}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h2>
          Товары в справочнике{" "}
          {products && <span className="muted">({products.length})</span>}
        </h2>
        {cabinets.length > 1 && (
          <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
            <button
              type="button"
              className={filterClientId === null ? "primary" : ""}
              onClick={() => setFilterClientId(null)}
            >
              Все
            </button>
            {cabinets.map((c) => (
              <button
                type="button"
                key={c.ozon_client_id}
                className={filterClientId === c.ozon_client_id ? "primary" : ""}
                onClick={() => setFilterClientId(c.ozon_client_id)}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
        {products === null ? (
          <div>Загрузка…</div>
        ) : products.length === 0 ? (
          <div className="muted">
            Пусто — загрузите выгрузку из Ozon, чтобы начать сличение.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>юрлицо</th>
                <th>артикул</th>
                <th>штрихкод</th>
                <th>название</th>
                <th>бренд</th>
                <th>статус</th>
                <th>последний апдейт</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={`${p.ozon_client_id}-${p.article}`}>
                  <td className="muted">{cabinetName(p.ozon_client_id)}</td>
                  <td className="mono">{p.article}</td>
                  <td className="mono">{p.barcode || "—"}</td>
                  <td>{p.name || "—"}</td>
                  <td>{p.brand || "—"}</td>
                  <td>{p.status || "—"}</td>
                  <td>{formatMSK(p.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
